import { BaseObj } from '@/objects/BaseObj';
import { useBaseObjEntityStateHandler } from '@/objects/entitystatehandler/entitystatehandler';
import { isRef, ref, unref } from 'vue';
import type { EntityState } from './../enums/EntityState';

export class TrackingResultSetup extends BaseObj {
    public ID: number;
    public __id: number;

    public DontPushEstimates: boolean;
    public __dontpushestimates: boolean;

    public EQMECanGeneratePasses: boolean;
    public __eqmecangeneratepasses: boolean;

    public EventID: number;
    public __eventid: number;

    public HideTrackingOnWeb: boolean;
    public __hidetrackingonweb: boolean;

    public IgnoreEQMEDevices: boolean;
    public __ignoreeqmedevices: boolean;

    public LiveTracking: boolean;
    public __livetracking: boolean;

    public NoGpsDropOffPeriod: number;
    public __nogpsdropoffperiod: number;

    public TrackOnlyGps: boolean;
    public __trackonlygps: boolean;

    public TrackPushAmount: number = 3;
    public __trackpushamount: number = 3;

    public TrackPushInterval: number = 10;
    public __trackpushinterval: number = 10;

    public __entityStateHandler;
    public __selfEntityState: EntityState;
    public __entityState: any;
    public get EntityState(): EntityState {
        return unref(this.__entityState);
    }
    /***
     * @deprecated Use the static FromJsValues method instead of the constructor to get type safety and default values
     */
    public constructor(values: {
        ID?: number;
        DontPushEstimates?: boolean;
        EntityState?: EntityState;
        EQMECanGeneratePasses?: boolean;
        EventID?: number;
        HideTrackingOnWeb?: boolean;
        IgnoreEQMEDevices?: boolean;
        LiveTracking?: boolean;
        NoGpsDropOffPeriod?: number;
        TrackOnlyGps?: boolean;
        TrackPushAmount?: number;
        TrackPushInterval?: number;
    }) {
        super();
        this.__entityState = ref(values.EntityState ?? 'Unchanged');
        this.__selfEntityState = values.EntityState ?? 'Unchanged';
        this.__entityStateHandler = useBaseObjEntityStateHandler<TrackingResultSetup>(unref(this.__entityState), (states) => {
            if (isRef(this.__entityState)) {
                this.__entityState.value = states.EntityStateWithChildrenConsidered;
            } else {
                console.warn('TrackingResultSetup tried to set entitystate, but entitystate is not a ref! This should never happen');
            }
            this.__selfEntityState = states.SelfEntityState;
        });
        this.ID = values.ID ?? 0;
        this.__id = values.ID ?? 0;
        Object.defineProperty(this, 'ID', {
            get(): number {
                return this.__id;
            },
            enumerable: true,
            configurable: true,
        });

        this.DontPushEstimates = values.DontPushEstimates ?? false;
        this.__dontpushestimates = values.DontPushEstimates ?? false;
        Object.defineProperty(this, 'DontPushEstimates', {
            get(): boolean {
                return this.__dontpushestimates;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('DontPushEstimates', this.__dontpushestimates, value);
                this.__dontpushestimates = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.EQMECanGeneratePasses = values.EQMECanGeneratePasses ?? false;
        this.__eqmecangeneratepasses = values.EQMECanGeneratePasses ?? false;
        Object.defineProperty(this, 'EQMECanGeneratePasses', {
            get(): boolean {
                return this.__eqmecangeneratepasses;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('EQMECanGeneratePasses', this.__eqmecangeneratepasses, value);
                this.__eqmecangeneratepasses = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.EventID = values.EventID ?? 0;
        this.__eventid = values.EventID ?? 0;
        Object.defineProperty(this, 'EventID', {
            get(): number {
                return this.__eventid;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('EventID', this.__eventid, value);
                this.__eventid = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.HideTrackingOnWeb = values.HideTrackingOnWeb ?? false;
        this.__hidetrackingonweb = values.HideTrackingOnWeb ?? false;
        Object.defineProperty(this, 'HideTrackingOnWeb', {
            get(): boolean {
                return this.__hidetrackingonweb;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('HideTrackingOnWeb', this.__hidetrackingonweb, value);
                this.__hidetrackingonweb = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.IgnoreEQMEDevices = values.IgnoreEQMEDevices ?? false;
        this.__ignoreeqmedevices = values.IgnoreEQMEDevices ?? false;
        Object.defineProperty(this, 'IgnoreEQMEDevices', {
            get(): boolean {
                return this.__ignoreeqmedevices;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('IgnoreEQMEDevices', this.__ignoreeqmedevices, value);
                this.__ignoreeqmedevices = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.LiveTracking = values.LiveTracking ?? false;
        this.__livetracking = values.LiveTracking ?? false;
        Object.defineProperty(this, 'LiveTracking', {
            get(): boolean {
                return this.__livetracking;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('LiveTracking', this.__livetracking, value);
                this.__livetracking = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.NoGpsDropOffPeriod = values.NoGpsDropOffPeriod ?? 0;
        this.__nogpsdropoffperiod = values.NoGpsDropOffPeriod ?? 0;
        Object.defineProperty(this, 'NoGpsDropOffPeriod', {
            get(): number {
                return this.__nogpsdropoffperiod;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('NoGpsDropOffPeriod', this.__nogpsdropoffperiod, value);
                this.__nogpsdropoffperiod = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.TrackOnlyGps = values.TrackOnlyGps ?? false;
        this.__trackonlygps = values.TrackOnlyGps ?? false;
        Object.defineProperty(this, 'TrackOnlyGps', {
            get(): boolean {
                return this.__trackonlygps;
            },
            set(value: boolean) {
                this.__entityStateHandler.propertyChanged('TrackOnlyGps', this.__trackonlygps, value);
                this.__trackonlygps = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.TrackPushAmount = values.TrackPushAmount ?? 3;
        this.__trackpushamount = values.TrackPushAmount ?? 3;
        Object.defineProperty(this, 'TrackPushAmount', {
            get(): number {
                return this.__trackpushamount;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('TrackPushAmount', this.__trackpushamount, value);
                this.__trackpushamount = value;
            },
            enumerable: true,
            configurable: true,
        });

        this.TrackPushInterval = values.TrackPushInterval ?? 10;
        this.__trackpushinterval = values.TrackPushInterval ?? 10;
        Object.defineProperty(this, 'TrackPushInterval', {
            get(): number {
                return this.__trackpushinterval;
            },
            set(value: number) {
                this.__entityStateHandler.propertyChanged('TrackPushInterval', this.__trackpushinterval, value);
                this.__trackpushinterval = value;
            },
            enumerable: true,
            configurable: true,
        });
    }

    public _setNotifyParentOfModificationCallback(callback: () => void) {
        this.__entityStateHandler.setNotifyParentOfModificationCallback(callback);
    }

    public serialize(): string {
        return (
            '{' +
            `"ID": ${JSON.stringify(this.ID)}` +
            ',' +
            `"DontPushEstimates": ${JSON.stringify(this.DontPushEstimates)}` +
            ',' +
            `"EntityState": ${unref(this.__selfEntityState) == 'Unchanged' ? 0 : unref(this.__selfEntityState) == 'Deleted' ? 1 : unref(this.__selfEntityState) == 'Modified' ? 2 : unref(this.__selfEntityState) == 'Added' ? 3 : 0}` +
            ',' +
            `"EQMECanGeneratePasses": ${JSON.stringify(this.EQMECanGeneratePasses)}` +
            ',' +
            `"EventID": ${JSON.stringify(this.EventID)}` +
            ',' +
            `"HideTrackingOnWeb": ${JSON.stringify(this.HideTrackingOnWeb)}` +
            ',' +
            `"IgnoreEQMEDevices": ${JSON.stringify(this.IgnoreEQMEDevices)}` +
            ',' +
            `"LiveTracking": ${JSON.stringify(this.LiveTracking)}` +
            ',' +
            `"NoGpsDropOffPeriod": ${JSON.stringify(this.NoGpsDropOffPeriod)}` +
            ',' +
            `"TrackOnlyGps": ${JSON.stringify(this.TrackOnlyGps)}` +
            ',' +
            `"TrackPushAmount": ${JSON.stringify(this.TrackPushAmount)}` +
            ',' +
            `"TrackPushInterval": ${JSON.stringify(this.TrackPushInterval)}` +
            ',' +
            `"ChangedColumns": ${unref(this.__selfEntityState) !== 'Modified' ? 'null' : JSON.stringify(this.__entityStateHandler.getChangedPropertiesArray())}` +
            '}'
        );
    }

    /**
     * Sets the entity state to Deleted
     */
    public Delete() {
        if (this.__selfEntityState === 'Added') {
            throw new Error('Cannot delete an object that is in Added state. It has not been saved yet.');
        }
        this.__selfEntityState = 'Deleted';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Undeletes the object by setting resetting the EntityState
     *
     * If the object has modifications, the new state will be 'Modified', otherwise 'Unchanged'
     */
    public DeleteUndo() {
        this.__selfEntityState = 'Unchanged';
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Sets the entity state to Added
     *
     * Also sets the ID to the supplied ID. If no id parameter supplied, the previous id is kept
     *
     * Note: This does not do anything with the children, so use with caution
     *
     * The use case of this is to make copies of existing objects as new objects without having to fill every field.
     */
    public SetEntityStateAsAddedAndResetId(id?: number) {
        if (this.EntityState !== 'Unchanged') {
            throw new Error('Changing a modified object to Added is not supported.');
        }
        this.__selfEntityState = 'Added';
        if (id !== undefined) {
            this.__id = id;
        }
        this.__entityStateHandler.entityStateChanged(this.__selfEntityState);
    }

    /**
     * Creates an instance of TrackingResultSetup from the supplied values.
     * Missing values have default values
     * @param values The values you want to set
     * @returns An object instance with the supplied values set
     */
    public static FromJsValues(values: {
        ID?: number;
        DontPushEstimates?: boolean;
        EQMECanGeneratePasses?: boolean;
        EventID?: number;
        HideTrackingOnWeb?: boolean;
        IgnoreEQMEDevices?: boolean;
        LiveTracking?: boolean;
        NoGpsDropOffPeriod?: number;
        TrackOnlyGps?: boolean;
        TrackPushAmount?: number;
        TrackPushInterval?: number;
        EntityState?: 'Added' | 'Unchanged';
    }): TrackingResultSetup {
        return new TrackingResultSetup(values);
    }

    public static _FromServerJson(json: any): TrackingResultSetup {
        return new TrackingResultSetup({
            ID: json.ID,
            DontPushEstimates: json.DontPushEstimates,
            EQMECanGeneratePasses: json.EQMECanGeneratePasses,
            EventID: json.EventID,
            HideTrackingOnWeb: json.HideTrackingOnWeb,
            IgnoreEQMEDevices: json.IgnoreEQMEDevices,
            LiveTracking: json.LiveTracking,
            NoGpsDropOffPeriod: json.NoGpsDropOffPeriod,
            TrackOnlyGps: json.TrackOnlyGps,
            TrackPushAmount: json.TrackPushAmount,
            TrackPushInterval: json.TrackPushInterval,
            EntityState: 'Unchanged',
        });
    }

    /**
     * Creates an instance of TrackingResultSetup from another instance of TrackingResultSetup.
     * @param other The TrackingResultSetup you want to clone
     * @returns A cloned TrackingResultSetup
     */
    public static FromOther(other: TrackingResultSetup): TrackingResultSetup {
        if (other.EntityState !== 'Unchanged') {
            throw new Error('Cloning an object with EntityState other than Unchanged is not supported');
        }
        return TrackingResultSetup._FromServerJson(JSON.parse(other.serialize()));
    }

    /**
     * Deepclones the object.
     */
    public DeepClone() {
        return TrackingResultSetup.FromOther(this);
    }

    /**
     * IsPropertyChanged.
     */
    public IsPropertyChanged(
        ...props: (
            | 'ID'
            | 'DontPushEstimates'
            | 'EQMECanGeneratePasses'
            | 'EventID'
            | 'HideTrackingOnWeb'
            | 'IgnoreEQMEDevices'
            | 'LiveTracking'
            | 'NoGpsDropOffPeriod'
            | 'TrackOnlyGps'
            | 'TrackPushAmount'
            | 'TrackPushInterval'
        )[]
    ) {
        return this.__entityStateHandler.isPropertyChanged(...props);
    }
    static NAME_OF_CLASS = 'TrackingResultSetup';
}
